import React from "react";
import { useEffect, useState } from "react";
import { Col, Form, FormCheck, ListGroup, Row, Spinner } from "react-bootstrap";
import { Bar, BarChart, Cell, Legend, Pie, PieChart, Tooltip, XAxis } from "recharts";

import DstButton from "../../components/DstButton/DstButton";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import { useSession } from "../../contexts/SessionContext";
import ApiService from "../../services/ApiService";
import translate from "../../services/Translate";
import languagesArray from "../../services/languages";
import { FilterData, SurveyAnswer } from "./InsightsSurvey.d";
import {
  COLOR_PALETTE,
  calculateGlobalNote,
  downloadSurveyAnswersCsv,
  fetchAnswer,
  fetchQuestions,
  getChartData,
  getFilteredCohortNames,
  getFilteredCohortPrograms,
  getFilteredMeetings,
  resetAndFetchSurveyData,
  sortCohortsWithDeletedLast,
  updateFilters,
} from "./InsightsSurvey.function";

const InsigthsSurvey = () => {
  const { language } = useSession();
  const [languageSelection, setLanguageSelection] = useState<string>(language);
  const [surveysType, setSurveysType] = useState<any[]>([]);
  const [surveyTypeSelection, setSurveyTypeSelection] = useState<any>(null);
  const [surveyTypeQuestionSelection, setSurveyTypeQuestionSelection] = useState<any>(null);
  const [surveyQuestions, setSurveyQuestions] = useState<any[]>([]);
  const [surveyQuestionsSpinner, setSurveyQuestionsSpinner] = useState<boolean>(true);
  const [surveyTypeOfQuestion, setSurveyTypeOfQuestion] = useState<string | null>(null);
  const [surveySelection, setSurveySelection] = useState<
    { answer: any[]; label: string; type: string; rate_limit: number }[]
  >([]);
  const [surveyAnswer, setSurveyAnswer] = useState<SurveyAnswer[]>([]);
  const [filterData, setFilterData] = useState<FilterData>({
    projects: [],
    cohort_names: [],
    cohort_programs: [],
    meetings: [],
    cohort_program_relationships: [],
    cohort_meeting_relationships: [],
    program_meeting_relationships: [],
  });
  const [originalFilterData, setOriginalFilterData] = useState<FilterData>({
    projects: [],
    cohort_names: [],
    cohort_programs: [],
    meetings: [],
    cohort_program_relationships: [],
    cohort_meeting_relationships: [],
    program_meeting_relationships: [],
  });
  const [surveyAnswserSpinner, setSurveyAnswserSpinner] = useState<boolean>(true);
  const [surveyChart, setSurveyChart] = useState<string>("PieChart");
  const [isCsvSurveyAnswersDownloading, setIsCsvSurveyAnswersDownloading] = useState<boolean>(false);
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [getMeetingStartDate, setGetMeetingStartDate] = useState<boolean>(false);
  const dateError = !!dateFrom && !!dateTo && dateFrom > dateTo;

  useEffect(() => {
    ApiService.get("surveys/types?fetch_versions=true").then((response) => setSurveysType(response.data.survey_types));
  }, []);

  const _renderCustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const { percentage, response } = payload[0].payload;

    return (
      <div className="m-regular p-3 white-background border">
        {response || translate(language, "PAGES.INSIGHTS.SURVEY.NO_RESPONSES")} : {percentage}%
      </div>
    );
  };

  const _renderCustomLegend = (data: any[]) => {
    return (
      <div className="text-center">
        <div className="d-flex justify-content-around">
          {surveyTypeOfQuestion && ["star_rate", "square_rate"].includes(surveyTypeOfQuestion)
            ? data
                .sort((a, b) => a.response - b.response)
                .map((item, index) => (
                  <div key={`legend-item-${index}`} style={{ color: item.color }}>
                    {item.response}
                  </div>
                ))
            : surveySelection.map((item, index) => (
                <div key={`item-${index}`} style={{ color: COLOR_PALETTE[index % COLOR_PALETTE.length] }}>
                  {item.label}
                </div>
              ))}
        </div>
        {surveyAnswer.length > 0 &&
          surveyTypeOfQuestion &&
          ["star_rate", "square_rate"].includes(surveyTypeOfQuestion) && (
            <p className="m-regular mt-3">
              {translate(language, "PAGES.INSIGHTS.SURVEY.GLOBAL_NOTE")} {calculateGlobalNote(surveyAnswer)} /{" "}
              {surveySelection[0]?.rate_limit}
            </p>
          )}
      </div>
    );
  };

  return (
    <div className="p-md-5 p-3">
      <div className="d-md-flex justify-content-between mb-3">
        <h1>{translate(language, "PAGES.INSIGHTS.SURVEY.TITLE")}</h1>
        <div className="d-flex align-items-end flex-wrap mb-3">
          <div className="me-2">
            <label htmlFor="survey-date-calendar-from" className="form-label dark-gray-font xs-regular mb-1">
              {translate(language, "PAGES.INSIGHTS.SURVEY.DATE_FROM")}
            </label>
            <input
              type="date"
              id="survey-date-calendar-from"
              className={`form-control s-regular ${dateError ? "is-invalid" : ""}`}
              value={dateFrom || ""}
              onChange={(e) => setDateFrom(e.target.value)}
              disabled={!surveyTypeSelection}
            />
          </div>
          <div className="me-2">
            <label htmlFor="survey-date-calendar-to" className="form-label dark-gray-font xs-regular mb-1">
              {translate(language, "PAGES.INSIGHTS.SURVEY.DATE_TO")}
            </label>
            <input
              type="date"
              id="survey-date-calendar-to"
              className={`form-control s-regular ${dateError ? "is-invalid" : ""}`}
              value={dateTo || ""}
              onChange={(e) => setDateTo(e.target.value)}
              disabled={!surveyTypeSelection}
            />
          </div>
          <div>
            <DstButton
              btnClass="py-2 mt-2"
              value={translate(language, "PAGES.INSIGHTS.SURVEY.DOWNLOAD_CSV_BOUTON")}
              disabled={!surveyTypeSelection || dateError}
              loading={isCsvSurveyAnswersDownloading}
              mock={!surveysType.length}
              clickFunction={() =>
                downloadSurveyAnswersCsv(
                  languageSelection,
                  surveyTypeSelection.survey_name,
                  language,
                  setIsCsvSurveyAnswersDownloading,
                  dateFrom,
                  dateTo,
                  getMeetingStartDate
                )
              }
            />
            {/* Only show the checkbox if the survey type includes "meeting" or "masterclass" */}
            {!isCsvSurveyAnswersDownloading &&
              ["meeting", "masterclass"].some((type: string) => surveyTypeSelection?.survey_name.includes(type)) && (
                <FormCheck
                  id="get-meeting-start-date"
                  className="position-absolute xs-regular mt-1"
                  label={translate(language, "PAGES.INSIGHTS.SURVEY.GET_MEETING_START_DATE")}
                  checked={getMeetingStartDate}
                  onChange={() => setGetMeetingStartDate((prev) => !prev)}
                />
              )}
          </div>
        </div>
      </div>
      <Row className="mb-3 m-bold">
        <Col xl={3}>
          <Form.Group controlId="languageSelect">
            <Form.Label>{translate(language, "PAGES.INSIGHTS.SURVEY.SELECT_LANGUAGE")}</Form.Label>
            <Form.Control
              as="select"
              className="m-regular w-100"
              value={languageSelection}
              onChange={(e) => {
                setLanguageSelection(e.target.value);
                setSurveySelection([]);
                setSurveyTypeSelection(null);
                setSurveyTypeQuestionSelection(null);
              }}
            >
              {Object.entries(languagesArray).map(([key, lang]) => (
                <option key={key} value={key}>
                  {lang.LANGUAGE}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col xl={3}>
          <Form.Group controlId="surveyTypeSelect">
            <Form.Label>{translate(language, "PAGES.INSIGHTS.SURVEY.SELECT_SURVEY_TYPE")}</Form.Label>
            {surveysType.length === 0 ? (
              <div>
                <Spinner />
              </div>
            ) : (
              <Form.Control
                as="select"
                value={surveyTypeSelection?.survey_name || ""}
                className="m-regular"
                onChange={(e) => {
                  setSurveyTypeSelection(surveysType.find((surveyType) => surveyType.survey_name === e.target.value));
                  setSurveyAnswer([]);
                  setSurveySelection([]);
                  setSurveyTypeQuestionSelection(null);
                }}
              >
                <option value="" disabled>
                  {translate(language, "PAGES.INSIGHTS.SURVEY.SELECT_SURVEY_TYPE_PLACEHOLDER")}
                </option>
                {Array.isArray(surveysType) &&
                  surveysType.map((surveyType: any, index: number) => (
                    <option key={surveyType.value || index} value={surveyType.value}>
                      {surveyType.survey_name}
                    </option>
                  ))}
              </Form.Control>
            )}
          </Form.Group>
        </Col>
        <Col xl={3}>
          {!!surveyTypeSelection &&
            (surveyTypeSelection?.versions?.length ? (
              <Form.Group controlId="surveyVersionSelect">
                <Form.Label>{translate(language, "PAGES.INSIGHTS.SURVEY.SELECT_VERSION")}</Form.Label>
                <Form.Control
                  as="select"
                  className="m-regular w-100"
                  value={surveyTypeQuestionSelection?.id || ""}
                  onChange={(e) => {
                    const newSurveyTypeQuestionSelection = surveyTypeSelection?.versions.find(
                      (version: any) => version.id === e.target.value
                    );
                    setSurveyQuestionsSpinner(true);
                    setSurveyTypeQuestionSelection(newSurveyTypeQuestionSelection);
                    fetchQuestions(newSurveyTypeQuestionSelection.id, setSurveyQuestions, setSurveyQuestionsSpinner);
                    setSurveyAnswer([]);
                    setSurveySelection([]);
                  }}
                >
                  <option value="" disabled>
                    {translate(language, "PAGES.INSIGHTS.SURVEY.SELECT_VERSION_PLACEHOLDER")}
                  </option>
                  {surveyTypeSelection?.versions.map((version: any, index: number) => (
                    <option key={version.id || index} value={version.id}>
                      {version.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            ) : (
              <Form.Group controlId="surveyVersionSelect">
                <Form.Label>{translate(language, "PAGES.INSIGHTS.SURVEY.SELECT_VERSION")}</Form.Label>
                <p className="s-regular">{translate(language, "PAGES.INSIGHTS.SURVEY.NO_VERSIONS_AVAILABLE")}</p>
              </Form.Group>
            ))}
        </Col>
        <Col xl={3}>
          {!!surveyTypeQuestionSelection &&
            (surveyQuestionsSpinner === true ? (
              <Form.Group controlId="surveySelect">
                <Form.Label>{translate(language, "PAGES.INSIGHTS.SURVEY.SELECT_QUESTION")}</Form.Label>
                <div>
                  <Spinner />
                </div>
              </Form.Group>
            ) : surveyQuestions.length > 0 ? (
              <Form.Group controlId="surveySelect">
                <Form.Label>{translate(language, "PAGES.INSIGHTS.SURVEY.SELECT_QUESTION")}</Form.Label>
                <Form.Control
                  as="select"
                  className="m-regular w-100"
                  value={surveySelection[0]?.label || ""}
                  onChange={(e) => {
                    const selectedQuestion = surveyQuestions.find(
                      (survey) =>
                        survey.survey_name === surveyTypeSelection.survey_name && survey.text === e.target.value
                    );
                    if (selectedQuestion) {
                      setSurveyAnswserSpinner(true);
                      setSurveyAnswer([]);
                      setFilterData({
                        projects: [],
                        cohort_names: [],
                        cohort_programs: [],
                        meetings: [],
                        cohort_program_relationships: [],
                      });
                      setOriginalFilterData({
                        projects: [],
                        cohort_names: [],
                        cohort_programs: [],
                        meetings: [],
                        cohort_program_relationships: [],
                      });
                      setActiveFilters({});
                      setSurveyTypeOfQuestion(selectedQuestion.type);
                      setSurveySelection([
                        {
                          answer: selectedQuestion.answer,
                          label: e.target.value,
                          type: selectedQuestion.type,
                          rate_limit: selectedQuestion.rate_limit,
                        },
                      ]);
                      fetchAnswer(
                        selectedQuestion.question_id,
                        (answers: SurveyAnswer[]) => {
                          setSurveyAnswer(answers);
                          const projectIds = Array.from(
                            new Set(answers.map((answer) => answer.project_id as string))
                          ).filter((project_id) => !!project_id);
                          const cohortData = Array.from(new Set(answers.map((answer) => String(answer.cohort_id))))
                            .filter((cohort_id) => !!cohort_id)
                            .map((cohort_id) => {
                              const answer = answers.find((a) => String(a.cohort_id) === cohort_id);
                              return {
                                id: cohort_id,
                                name: answer?.cohort_name || "",
                              };
                            });
                          const cohortPrograms = Array.from(
                            new Set(answers.map((answer) => answer.cohort_program as string))
                          ).filter((cohort_program) => !!cohort_program);
                          const meetingData = Array.from(new Set(answers.map((answer) => answer.meeting_id as string)))
                            .filter((meeting_id) => !!meeting_id)
                            .map((id) => ({
                              id,
                              name: answers.find((answer) => answer.meeting_id === id)?.meeting_name || "",
                            }));

                          const relatedCohortPrograms = answers
                            .filter((answer) => answer.cohort_id && answer.cohort_program)
                            .map((answer) => ({
                              cohort_id: String(answer.cohort_id),
                              program: answer.cohort_program as string,
                            }))
                            .filter(
                              (rel, index, self) =>
                                self.findIndex((r) => r.cohort_id === rel.cohort_id && r.program === rel.program) ===
                                index
                            );

                          const relatedCohortMeetings = answers
                            .filter((answer) => answer.cohort_id && answer.meeting_id)
                            .map((answer) => ({
                              cohort_id: String(answer.cohort_id),
                              meeting_id: String(answer.meeting_id),
                            }))
                            .filter(
                              (rel, index, self) =>
                                self.findIndex(
                                  (r) => r.cohort_id === rel.cohort_id && r.meeting_id === rel.meeting_id
                                ) === index
                            );

                          const relatedProgramMeetings = answers
                            .filter((answer) => answer.cohort_program && answer.meeting_id)
                            .map((answer) => ({
                              program: String(answer.cohort_program),
                              meeting_id: String(answer.meeting_id),
                            }))
                            .filter(
                              (rel, index, self) =>
                                self.findIndex((r) => r.program === rel.program && r.meeting_id === rel.meeting_id) ===
                                index
                            );

                          const completeFilterData = {
                            projects: projectIds,
                            cohort_names: cohortData,
                            cohort_programs: cohortPrograms,
                            meetings: meetingData,
                            cohort_program_relationships: relatedCohortPrograms,
                            cohort_meeting_relationships: relatedCohortMeetings,
                            program_meeting_relationships: relatedProgramMeetings,
                          };

                          setFilterData(completeFilterData);
                          setOriginalFilterData(completeFilterData);
                        },
                        setSurveyAnswserSpinner
                      );
                    }
                  }}
                >
                  <option value="" disabled>
                    {translate(language, "PAGES.INSIGHTS.SURVEY.SELECT_QUESTION_PLACEHOLDER")}
                  </option>
                  {surveyQuestions.map((survey, index) => (
                    <option key={survey.text || index} value={survey.text} disabled={survey.type === "text"}>
                      {survey.text}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            ) : (
              <Form.Group controlId="surveySelect">
                <Form.Label>{translate(language, "PAGES.INSIGHTS.SURVEY.SELECT_QUESTION")}</Form.Label>
                <p className="s-regular">{translate(language, "PAGES.INSIGHTS.SURVEY.NO_QUESTIONS_AVAILABLE")}</p>
              </Form.Group>
            ))}
        </Col>
      </Row>
      {(surveyAnswer.length > 0 || Object.keys(activeFilters).length > 0) && (
        <div className="mb-3">
          <h3 className="mb-2">{translate(language, "PAGES.INSIGHTS.SURVEY.FILTERS")}</h3>
          <div className="d-flex flex-wrap">
            {surveyAnswer.length > 0 && surveyAnswer[0]?.project_id && filterData.projects.filter(Boolean).length > 0 && (
              <div className="me-2">
                <MultiFilterDropdown
                  tags={filterData.projects.filter(Boolean).map((id: string) => ({
                    id: id,
                    value: id,
                    display: String(id),
                  }))}
                  onSelectTags={(values) =>
                    updateFilters(
                      "project_id",
                      values.map(String),
                      setActiveFilters,
                      surveySelection,
                      setSurveyAnswserSpinner,
                      surveyQuestions,
                      setSurveyAnswer
                    )
                  }
                  language={language}
                  inputTitle={translate(language, "PAGES.INSIGHTS.SURVEY.FILTER.PROJECT")}
                  showSearchBar
                />
              </div>
            )}
            {surveyAnswer.length > 0 && surveyAnswer[0]?.cohort_id && (
              <React.Fragment>
                {filterData.cohort_names.filter(Boolean).length > 0 && (
                  <div className="me-2">
                    <MultiFilterDropdown
                      tags={sortCohortsWithDeletedLast(
                        getFilteredCohortNames(
                          originalFilterData.cohort_names,
                          originalFilterData.cohort_program_relationships,
                          activeFilters.cohort_program ? activeFilters.cohort_program.split(",") : [],
                          originalFilterData.cohort_meeting_relationships,
                          activeFilters.meeting_id ? activeFilters.meeting_id.split(",") : []
                        )
                      ).map((cohort: { id: string; name: string }) => ({
                        value: cohort.id,
                        display:
                          cohort.name?.trim() !== ""
                            ? `${cohort.name} (${cohort.id})`
                            : `${translate(language, "PAGES.INSIGHTS.SURVEY.FILTER.COHORT_DELETED")} (${cohort.id})`,
                      }))}
                      onSelectTags={(values) =>
                        updateFilters(
                          "cohort_id",
                          values.map(String),
                          setActiveFilters,
                          surveySelection,
                          setSurveyAnswserSpinner,
                          surveyQuestions,
                          setSurveyAnswer
                        )
                      }
                      language={language}
                      inputTitle={translate(language, "PAGES.INSIGHTS.SURVEY.FILTER.COHORT_NAME")}
                      showSearchBar
                    />
                  </div>
                )}
                {filterData.cohort_programs.filter(Boolean).length > 0 && (
                  <div className="me-2">
                    <MultiFilterDropdown
                      tags={getFilteredCohortPrograms(
                        originalFilterData.cohort_programs.filter(Boolean),
                        originalFilterData.cohort_program_relationships,
                        activeFilters.cohort_id ? activeFilters.cohort_id.split(",") : [],
                        originalFilterData.program_meeting_relationships,
                        activeFilters.meeting_id ? activeFilters.meeting_id.split(",") : []
                      ).map((program: string) => ({
                        id: program,
                        value: program,
                        display: program,
                      }))}
                      onSelectTags={(values) =>
                        updateFilters(
                          "cohort_program",
                          values.map(String),
                          setActiveFilters,
                          surveySelection,
                          setSurveyAnswserSpinner,
                          surveyQuestions,
                          setSurveyAnswer
                        )
                      }
                      language={language}
                      inputTitle={translate(language, "PAGES.INSIGHTS.SURVEY.FILTER.COHORT_PROGRAM")}
                      showSearchBar
                    />
                  </div>
                )}
              </React.Fragment>
            )}
            {surveyAnswer.length > 0 &&
              surveyAnswer[0]?.meeting_id &&
              filterData.meetings.filter((meeting) => meeting.id && meeting.name).length > 0 && (
                <div className="me-2">
                  <MultiFilterDropdown
                    tags={getFilteredMeetings(
                      originalFilterData.meetings,
                      originalFilterData.cohort_meeting_relationships,
                      originalFilterData.program_meeting_relationships,
                      activeFilters.cohort_id ? activeFilters.cohort_id.split(",") : [],
                      activeFilters.cohort_program ? activeFilters.cohort_program.split(",") : []
                    )
                      .filter((meeting) => meeting.id && meeting.name)
                      .map((meeting: { id: string; name: string }) => ({
                        id: meeting.id,
                        value: meeting.id,
                        display: meeting.name,
                        customSearchFields: [meeting.id, meeting.name],
                      }))}
                    onSelectTags={(values) =>
                      updateFilters(
                        "meeting_id",
                        values.map(String),
                        setActiveFilters,
                        surveySelection,
                        setSurveyAnswserSpinner,
                        surveyQuestions,
                        setSurveyAnswer
                      )
                    }
                    language={language}
                    inputTitle={translate(language, "PAGES.INSIGHTS.SURVEY.FILTER.MEETING")}
                    showSearchBar
                  />
                </div>
              )}
            {Object.keys(activeFilters).length > 0 && (
              <DstButton
                variant="light"
                value={translate(language, "PAGES.INSIGHTS.SURVEY.CLEAR_FILTERS")}
                btnClass="ms-3 clickable dark-gray-font text-decoration-underline xs-regular"
                clickFunction={() =>
                  resetAndFetchSurveyData(
                    setActiveFilters,
                    surveySelection,
                    surveyQuestions,
                    setSurveyAnswserSpinner,
                    setSurveyAnswer,
                    setFilterData,
                    setOriginalFilterData
                  )
                }
              />
            )}
          </div>
        </div>
      )}
      {surveyAnswserSpinner === true && surveySelection.length > 0 ? (
        <div>
          <Spinner />
        </div>
      ) : (
        surveyTypeSelection &&
        surveyQuestions.length > 0 &&
        surveySelection.length > 0 && (
          <Row>
            <Col md={6}>
              <h3>{translate(language, "PAGES.INSIGHTS.SURVEY.RESULTS")}</h3>
              {surveyAnswer.length > 0 ? (
                <p className="m-regular">
                  {translate(language, "PAGES.INSIGHTS.SURVEY.TOTAL_RESPONSES").replace(
                    "{{COUNT}}",
                    String(surveyAnswer.length)
                  )}
                </p>
              ) : (
                <p className="m-regular">{translate(language, "PAGES.INSIGHTS.SURVEY.NO_RESPONSES")}</p>
              )}
              {surveyAnswer.length > 0 ? (
                <div className="d-flex">
                  {surveyChart === "PieChart" ? (
                    <PieChart width={400} height={300}>
                      <Pie
                        data={getChartData(surveyAnswer, surveyTypeOfQuestion)}
                        cx={200}
                        cy={100}
                        labelLine={false}
                        label={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {getChartData(surveyAnswer, surveyTypeOfQuestion).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={_renderCustomTooltip} />
                      <Legend content={() => _renderCustomLegend(getChartData(surveyAnswer, surveyTypeOfQuestion))} />
                    </PieChart>
                  ) : (
                    <div>
                      <BarChart width={400} height={300} data={getChartData(surveyAnswer, surveyTypeOfQuestion)}>
                        <XAxis dataKey="response" tickLine={false} axisLine={false} hide />
                        <Tooltip content={_renderCustomTooltip} />
                        <Legend content={() => _renderCustomLegend(getChartData(surveyAnswer, surveyTypeOfQuestion))} />
                        <Bar dataKey="count" fill="#8884d8">
                          {getChartData(surveyAnswer, surveyTypeOfQuestion).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </div>
                  )}
                  <div>
                    <DstButton
                      btnClass="dst-icon-switch-horizontal-01"
                      variant="secondary"
                      clickFunction={() => setSurveyChart(surveyChart === "PieChart" ? "BarChart" : "PieChart")}
                      value=""
                    />
                  </div>
                </div>
              ) : null}
            </Col>
            {surveyAnswer.length > 0 && (
              <Col md={6}>
                <h3>{translate(language, "PAGES.INSIGHTS.SURVEY.RESPONSES")}</h3>
                <ListGroup className="text-break">
                  {Object.entries(
                    surveyAnswer.reduce((acc: { [key: string]: number }, answer) => {
                      const response =
                        answer.content.answer || translate(language, "PAGES.INSIGHTS.SURVEY.NO_RESPONSES");
                      acc[response] = (acc[response] || 0) + 1;
                      return acc;
                    }, {} as { [key: string]: number })
                  )
                    .sort((a, b) => b[1] - a[1])
                    .map(([response, count], index) => (
                      <ListGroup.Item key={`response-${index}`}>
                        {count > 1 ? (
                          <React.Fragment>
                            {response} <span style={{ color: "var(--color-gray)" }}>({count})</span>
                          </React.Fragment>
                        ) : (
                          response
                        )}
                      </ListGroup.Item>
                    ))}
                </ListGroup>
              </Col>
            )}
          </Row>
        )
      )}
    </div>
  );
};

export default InsigthsSurvey;
